import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  LinearProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Card,
  CardContent
} from '@mui/material';
import {
  ExpandMore,
  Language,
  Description,
  Error,
  CheckCircle,
  Folder,
  FolderOpen,
  Search,
  Web,
  Speed
} from '@mui/icons-material';
import { getMapStatus } from '../api';

const SiteMap = ({ mapJobId, onSiteMapReady }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [siteMap, setSiteMap] = useState(null);
  const [expandedDepths, setExpandedDepths] = useState(new Set([0]));
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState('Initializing site mapper...');
  const [discoveredPages, setDiscoveredPages] = useState(0);

  useEffect(() => {
    if (!mapJobId) return;

    let pollCount = 0;
    const maxPollCount = 300; // Maximum 10 minutes of polling (300 * 2 seconds)
    
    // Simulate progress updates
    const progressMessages = [
      'Initializing site mapper...',
      'Analyzing robots.txt...',
      'Discovering page links...',
      'Mapping site structure...',
      'Finalizing results...'
    ];

    const pollStatus = async () => {
      try {
        const status = await getMapStatus(mapJobId);
        
        // Update progress with real data
        if (status.status === 'running') {
          if (status.discovered_pages !== undefined && status.progress !== undefined) {
            // Use real queue-based progress data
            setDiscoveredPages(status.discovered_pages);
            setLoadingProgress(status.progress);
            
            const pendingText = status.queue_size > 0 ? ` (${status.queue_size} pending)` : '';
            setLoadingMessage(`Discovered ${status.discovered_pages} pages${pendingText} - Depth ${status.depth || 0}`);
          } else {
            // Fallback to simulated progress for initial state
            const progressIndex = Math.min(Math.floor(pollCount / 2), progressMessages.length - 1);
            setLoadingMessage(progressMessages[progressIndex]);
            setLoadingProgress(Math.min((pollCount / 4) * 100, 90));
            pollCount++;
          }
        }
        
        if (status.status === 'completed') {
          setLoadingProgress(100);
          setLoadingMessage('Site mapping completed!');
          setTimeout(() => {
            setSiteMap(status.result);
            setLoading(false);
            onSiteMapReady(status.result);
          }, 500);
        } else if (status.status === 'failed') {
          setError(status.error || 'Site mapping failed');
          setLoading(false);
        } else if (status.status === 'running') {
          // Continue polling
          if (pollCount < maxPollCount) {
            setTimeout(pollStatus, 2000);
          } else {
            setError('Site mapping is taking longer than expected. Large sites can take up to 10 minutes to map. Please wait or try again.');
            setLoading(false);
          }
        }
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    pollStatus();
  }, [mapJobId, onSiteMapReady]);

  const handleDepthToggle = (depth) => {
    const newExpanded = new Set(expandedDepths);
    if (newExpanded.has(depth)) {
      newExpanded.delete(depth);
    } else {
      newExpanded.add(depth);
    }
    setExpandedDepths(newExpanded);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getStatusIcon = (page) => {
    if (page.accessible) {
      return <CheckCircle color="success" fontSize="small" />;
    } else {
      return <Error color="error" fontSize="small" />;
    }
  };

  if (loading) {
    return (
      <Paper elevation={2} sx={{ p: 4 }}>
        <Card sx={{ mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <Web sx={{ fontSize: 60, mb: 2, opacity: 0.9 }} />
            <Typography variant="h5" gutterBottom>
              Mapping Website Structure
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9, mb: 3 }}>
              Discovering and analyzing all pages on the website
            </Typography>
            
            <Box sx={{ width: '100%', mb: 2 }}>
              <LinearProgress 
                variant="determinate" 
                value={loadingProgress} 
                sx={{ 
                  height: 8, 
                  borderRadius: 4,
                  backgroundColor: 'rgba(255,255,255,0.3)',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: '#4fc3f7',
                    borderRadius: 4,
                  }
                }} 
              />
            </Box>
            
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              {loadingMessage}
            </Typography>
            {discoveredPages > 0 && (
              <Typography variant="caption" sx={{ opacity: 0.7, mt: 1, display: 'block' }}>
                {Math.round(loadingProgress)}% complete
              </Typography>
            )}
          </CardContent>
        </Card>

        <Box sx={{ display: 'flex', justifyContent: 'space-around', textAlign: 'center' }}>
          <Box>
            <Search color="primary" sx={{ fontSize: 40, mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              Discovering Links
            </Typography>
          </Box>
          <Box>
            <Speed color="primary" sx={{ fontSize: 40, mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              Respecting robots.txt
            </Typography>
          </Box>
          <Box>
            <Language color="primary" sx={{ fontSize: 40, mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              Building Structure
            </Typography>
          </Box>
        </Box>

        <Alert severity="info" sx={{ mt: 3 }}>
          <strong>What we're doing:</strong> Our scraper is analyzing the website structure, 
          checking robots.txt compliance, and discovering all accessible pages. This can take 2-10 minutes for larger sites.
        </Alert>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper elevation={2} sx={{ p: 3 }}>
        <Alert severity="error">
          {error}
        </Alert>
      </Paper>
    );
  }

  if (!siteMap) {
    return null;
  }

  return (
    <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        Website Structure
      </Typography>

      {/* Summary Statistics */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
        <Chip 
          icon={<Language />}
          label={`${siteMap.total_pages} Total Pages`} 
          variant="outlined"
        />
        <Chip 
          icon={<CheckCircle />}
          label={`${siteMap.accessible_pages} Accessible`} 
          color="success"
          variant="outlined"
        />
        {siteMap.blocked_pages > 0 && (
          <Chip 
            icon={<Error />}
            label={`${siteMap.blocked_pages} Blocked`} 
            color="error"
            variant="outlined"
          />
        )}
        <Chip 
          icon={<Description />}
          label={`${formatFileSize(siteMap.total_size)} Total Size`} 
          variant="outlined"
        />
        <Chip 
          label={`Max Depth: ${siteMap.max_depth}`} 
          variant="outlined"
        />
      </Box>

      {/* Pages by Depth */}
      <Box>
        {Object.entries(siteMap.pages_by_depth || {}).map(([depth, pages]) => (
          <Accordion 
            key={depth}
            expanded={expandedDepths.has(parseInt(depth))}
            onChange={() => handleDepthToggle(parseInt(depth))}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {expandedDepths.has(parseInt(depth)) ? 
                  <FolderOpen color="primary" /> : 
                  <Folder color="primary" />
                }
                <Typography variant="subtitle1">
                  Depth {depth} ({pages.length} pages)
                </Typography>
              </Box>
            </AccordionSummary>
            
            <AccordionDetails>
              <List dense>
                {pages.map((page, index) => (
                  <ListItem key={index} divider>
                    <ListItemIcon>
                      {getStatusIcon(page)}
                    </ListItemIcon>
                    
                    <ListItemText
                      primary={
                        <Box>
                          <Typography variant="body2" component="div">
                            <strong>{page.title || 'No title'}</strong>
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {page.url}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                          {page.accessible && (
                            <>
                              <Chip 
                                label={formatFileSize(page.size)} 
                                size="small" 
                                variant="outlined"
                              />
                              {page.status_code && (
                                <Chip 
                                  label={`${page.status_code}`} 
                                  size="small" 
                                  color={page.status_code === 200 ? 'success' : 'warning'}
                                  variant="outlined"
                                />
                              )}
                            </>
                          )}
                          {!page.accessible && page.error && (
                            <Chip 
                              label={page.error} 
                              size="small" 
                              color="error"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
        <strong>Note:</strong> Only accessible pages that comply with robots.txt are shown. 
        Blocked or inaccessible pages are excluded from scraping options.
      </Typography>
    </Paper>
  );
};

export default SiteMap;
